import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Check as CheckIcon,
  Star as StarIcon,
  Celebration as CelebrationIcon,
} from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import { awsConfig } from '../config/aws'
import BrandLogo from '../components/BrandLogo'

interface ServicePackage {
  id: string
  name: string
  description: string
  price: number
  features: string[]
  popular: boolean
}

interface ProposalData {
  leadName: string
  proposal: string
  sentAt: string
  packages: ServicePackage[]
  alreadyAccepted: boolean
  selectedPackage: string | null
}

const API_BASE = awsConfig.API.REST.missfit.endpoint

export default function ProposalPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<ProposalData | null>(null)
  const [creatingCheckout, setCreatingCheckout] = useState<string | null>(null)

  const success = searchParams.get('success') === 'true'
  const canceled = searchParams.get('canceled') === 'true'

  useEffect(() => {
    async function fetchProposal() {
      if (!token) {
        setError('Invalid proposal link')
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${API_BASE}/public-proposal?token=${token}`)
        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.message || 'Failed to load proposal')
        }

        setData(result.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load proposal')
      } finally {
        setLoading(false)
      }
    }

    fetchProposal()
  }, [token])

  const handleSelectPackage = async (packageId: string) => {
    if (!token) return

    try {
      setCreatingCheckout(packageId)
      const response = await fetch(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          packageId,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create checkout session')
      }

      // Redirect to Stripe Checkout
      window.location.href = result.data.checkoutUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
      setCreatingCheckout(null)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f4f6f9',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Unable to Load Proposal
          </Typography>
          <Typography color="text.secondary">{error}</Typography>
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            href="https://dreamjobhq.com"
          >
            Visit MissFit Mindset
          </Button>
        </Paper>
      </Box>
    )
  }

  if (success || data?.alreadyAccepted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f4f6f9',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
          <CelebrationIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ color: '#2c5282' }}>
            Thank You for Your Purchase!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We're thrilled to have you on board, {data?.leadName || 'friend'}! Your career transformation journey begins now.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Our team will reach out within 24 hours to schedule your kickoff session.
          </Typography>
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary">
            Questions? Contact us at{' '}
            <a href="mailto:support@dreamjobhq.com" style={{ color: '#4a90a4' }}>
              support@dreamjobhq.com
            </a>
          </Typography>
        </Paper>
      </Box>
    )
  }

  if (canceled) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f4f6f9',
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mb: 4 }}>
            Your checkout was canceled. You can still select a package below to continue.
          </Alert>
          {data && <ProposalContent data={data} onSelectPackage={handleSelectPackage} creatingCheckout={creatingCheckout} />}
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f9', py: 6 }}>
      <Container maxWidth="lg">
        {data && <ProposalContent data={data} onSelectPackage={handleSelectPackage} creatingCheckout={creatingCheckout} />}
      </Container>
    </Box>
  )
}

interface ProposalContentProps {
  data: ProposalData
  onSelectPackage: (packageId: string) => void
  creatingCheckout: string | null
}

function ProposalContent({ data, onSelectPackage, creatingCheckout }: ProposalContentProps) {
  return (
    <>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        {/* Lockup replaces the typed wordmark, which was set in a legacy blue that
            matches nothing in the brand palette */}
        <BrandLogo height={52} sx={{ mx: 'auto', mb: 1.5 }} />
        <Typography variant="h6" color="text.secondary">
          Your Career Transformation Partner
        </Typography>
      </Box>

      {/* Greeting */}
      <Paper
        sx={{
          p: 4,
          mb: 6,
          background: 'linear-gradient(135deg, #2c5282 0%, #4a90a4 100%)',
          color: 'white',
          borderRadius: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Hello {data.leadName || 'there'}!
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.95 }}>
          Thank you for your interest in transforming your career. We've prepared a personalized
          proposal just for you. Review the details below and choose the package that best fits
          your goals.
        </Typography>
      </Paper>

      {/* Proposal Content */}
      <Paper sx={{ p: 4, mb: 6, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ color: '#2c5282', mb: 3, fontWeight: 600 }}>
          Your Personalized Proposal
        </Typography>
        <Box
          sx={{
            bgcolor: '#f8fafc',
            p: 3,
            borderRadius: 2,
            borderLeft: '4px solid #4a90a4',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.8,
          }}
        >
          <Typography variant="body1" component="div">
            {data.proposal}
          </Typography>
        </Box>
      </Paper>

      {/* Packages */}
      <Typography
        variant="h4"
        sx={{ textAlign: 'center', color: '#2c5282', mb: 4, fontWeight: 600 }}
      >
        Choose Your Package
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
          mb: 6,
        }}
      >
        {data.packages.map((pkg) => (
          <Card
            key={pkg.id}
            sx={{
              position: 'relative',
              borderRadius: 3,
              border: pkg.popular ? '2px solid #4a90a4' : '1px solid #e2e8f0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
              },
            }}
          >
            {pkg.popular && (
              <Chip
                icon={<StarIcon />}
                label="Most Popular"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontWeight: 600,
                }}
              />
            )}
            <CardContent sx={{ pt: pkg.popular ? 4 : 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c5282', mb: 1 }}>
                {pkg.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                {pkg.description}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c5282', mb: 3 }}>
                ${pkg.price.toLocaleString()}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense disablePadding>
                {pkg.features.map((feature, idx) => (
                  <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ p: 3, pt: 0 }}>
              <Button
                fullWidth
                variant={pkg.popular ? 'contained' : 'outlined'}
                size="large"
                onClick={() => onSelectPackage(pkg.id)}
                disabled={creatingCheckout !== null}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  ...(pkg.popular && {
                    background: 'linear-gradient(135deg, #2c5282 0%, #4a90a4 100%)',
                  }),
                }}
              >
                {creatingCheckout === pkg.id ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Get Started'
                )}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Trust Section */}
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: '#f8fafc' }}>
        <Typography variant="h6" sx={{ color: '#2c5282', mb: 2 }}>
          100% Satisfaction Guarantee
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          We're confident in our services. If you're not completely satisfied with your
          deliverables, we'll work with you until you are or provide a full refund within 30 days.
        </Typography>
      </Paper>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
        <Typography variant="body2">
          Questions? Contact us at{' '}
          <a href="mailto:support@dreamjobhq.com" style={{ color: '#4a90a4' }}>
            support@dreamjobhq.com
          </a>
        </Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
          &copy; {new Date().getFullYear()} MissFit Mindset. All rights reserved.
        </Typography>
      </Box>
    </>
  )
}
